import { useState } from "react";
import Tabs from "../../components/tabs/tabs.component";
import { ITabItem } from "../../components/tabs/tabs.interface";
import { registrationEnum } from "../../enums/glimps.enums";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getAllSubs, createNewUser } from "../../services/auth.service";
import {
  IGiftInvitation,
  IUserRegistration,
} from "../../interfaces/user.interface";
import { ISubscription } from "../../interfaces/subscription.interface";
import {
  displayError,
  displaySuccess,
  displayWarning,
} from "components/Toast/toast.component";
import PaymentScreen from "components/stripe-elements/payment.screen";
import RegisterUser from "components/register-user/resgister-user.component";
import HelpSomeoneLearn from "components/register-user/help-someone-learn.component";
import ChoosePlan from "components/register-user/choose-plan.component";
import RedeemCode from "components/register-user/redeem-code.component";
import PurchaserInfo from "components/register-user/purchaser-info.component";
import { FormikHelpers } from "formik";
import {
  sendGiftInvitation,
  validateGiftInvitation,
} from "services/gift-invitation.service";
import { validateRegistrationCode } from "services/promo.service";
import { EmailValues, ICode, PurchaserValues } from "interfaces/RegisterInterface";
import { useLocation, useNavigate } from "react-router";
import { IPriceInfo } from "interfaces/PaymentInterface";
import { SelectOption } from "components/select/select.component";

const GiftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 82 88" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4.705 80.638L41.372 87.15 41.047 32.822 0 25.184l4.705 55.454z" fill="#3A5A9E" />
    <path d="M77.046 80.206 41.372 87.15l-.324-54.328L82 24.695l-4.954 55.511z" fill="#213269" />
    <path d="M82 24.695 41.047 32.822 0 25.184l40.953-8.127L82 24.695z" fill="#3A5A9E" />
    <path d="M14.945 82.457 13.405 27.609l39.908-8.776 12.483 2.486-40.982 8.137 2.149 55.193-12.018-2.192z" fill="#5EA3E0" />
    <path d="M67.322 82.246l2.403-54.568-39.993-8.704-12.451 2.635 40.851 8.101-3.007 54.922 12.197-2.386z" fill="#5EA3E0" />
    <path d="M69.469 2.562C49.162-9.595 39.606 25.462 39.606 25.462c46.121-8.021 29.863-22.9 29.863-22.9zM51.634 19.596s4.583-12.306 14.08-11.908c0 0 5.336 8.031-14.08 11.908z" fill="#5EA3E0" />
    <path d="M12.215 2.904S-3.863 17.976 42.35 25.446c0 0-9.974-34.94-30.134-22.542zm5.57 1.584c13.376-1.99 16.099 11.612 16.099 11.612-13.663-3.43-16.1-12.612-16.1-12.612z" fill="#5EA3E0" />
  </svg>
)

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="45" cy="45" r="45" fill="#213269" />
    <circle cx="44.587" cy="44.587" r="36.33" fill="#5EA3E0" />
    <line x1="26.883" y1="48.716" x2="35.505" y2="57.337" stroke="white" strokeWidth="17" strokeLinecap="round" />
    <line x1="63.578" y1="33.489" x2="37.617" y2="59.45" stroke="white" strokeWidth="17" strokeLinecap="round" />
  </svg>
)

const PersonIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="12" fill="#213269" />
    <circle cx="12" cy="9" r="3.5" fill="#5EA3E0" />
    <path d="M5.5 20c0-2.8 2.9-5 6.5-5s6.5 2.2 6.5 5" fill="#5EA3E0" />
  </svg>
)

const tabsList: ITabItem[] = [
  {
    icon: "assets/icons/boy_with_glass.svg",
    name: "For Me",
  },
  {
    iconNode: <GiftIcon />,
    name: "For Someone Else",
  },
  {
    iconNode: <CheckIcon />,
    name: "Redeem a Code",
  },
];
const initialEmailValues: EmailValues = { emailList: [""] };
const initialPurchaserValues: PurchaserValues = {
  purchaser_name: "",
  purchaser_email: "",
};

const RegisterScreen: React.FC = () => {
  const { search } = useLocation();
  const getTypeAsNumber = () => {
    const typeString = search.split("type=")[1];
    const numberValue = parseInt(typeString, 10);
    return numberValue;
  };
  const typeAsNumber = getTypeAsNumber();
  const getRedeemCode = search.split("&code=")[1];

  const [activeTab, setActiveTab] = useState(typeAsNumber ? typeAsNumber : 0);
  const [redeemCode, setRedeemCode] = useState(
    getRedeemCode ? getRedeemCode : ""
  );

  const [userInfo, setUserInfo] = useState<IUserRegistration>({
    avatar: "",
    email: "",
    first_name: "",
    id: null,
    last_name: "",
    password: "",
    subscription_id: "",
    confirm_password: "",
    client_secret: "",
    dob: "",
    email_subscribed: false,
    education_stage: "",
    extra_info: "",
  });
  const [email, setEmail] = useState<EmailValues>(initialEmailValues);
  const [purchaserInfo, setPurchaserInfo] = useState<PurchaserValues>(
    initialPurchaserValues
  );
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState<string | undefined>();
  const [priceInfo, setPriceInfo] = useState<IPriceInfo>();

  const [activeRegStep, setActiveRegStep] = useState(
    registrationEnum.subscription
  );

  const { data: subscriptionList, status } = useQuery({
    queryKey: ["getAllSubs"],
    queryFn: getAllSubs,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const tabChanged = (e: React.MouseEvent<HTMLDivElement>, index: number) => {
    setActiveTab(index);
    setActiveRegStep(registrationEnum.subscription);
  };
  const onPlanSelect = (item: ISubscription, index: number) => {
    setUserInfo((prevData) => ({ ...prevData, subscription_id: item.id }));
    if (activeTab === 1) {
      setActiveRegStep(registrationEnum.purchaser);
    } else {
      setActiveRegStep(registrationEnum.user);
    }
  };

  const handlePurchaserSubmit = (values: PurchaserValues) => {
    setPurchaserInfo(values);
    setActiveRegStep(registrationEnum.user);
  };

  const handleValueChanged = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setUserInfo((prevData) => ({ ...prevData, [name]: value }));
  };

  const registerUser = useMutation({
    mutationFn: (userDetails: IUserRegistration) => {
      setUserInfo(userDetails)
      if (userDetails.registration_code) {
        const { invitation_id, ...payload } = userDetails;
        return createNewUser(payload);
      }
      return createNewUser(userDetails)
    },
    retry: 0,
    onSuccess: (e) => {
      if (e.payment_required === false) {
        displaySuccess("Your account has been created with a 100% discount!");
        navigate("/register-success");
      } else if (e.client_secret) {
        setClientSecret(e.client_secret);
        setPriceInfo(e);
        setActiveRegStep(registrationEnum.payment);
        displayWarning(
          "Your account has been created. Please proceed to payment for activation."
        );
      } else {
        displaySuccess("Your code has been redeemed successfully");
        navigate("/register-success");
      }
    },
  });

  const handleSubmit = async (e: IUserRegistration) => {
    await registerUser.mutateAsync(e);
  };

  const handleEmails = useMutation({
    mutationFn: (data: IGiftInvitation) => sendGiftInvitation(data),
    retry: 0,
    onSuccess: (ev: IPriceInfo) => {
      setClientSecret(ev.client_secret);
      setPriceInfo(ev);
      // setEmail(initialEmailValues);
      setActiveRegStep(registrationEnum.payment);
    },
    onError: () => {
      displayError("Sending invitation failed");
    },
  });

  const handleGiftInvitation = async (
    emails: EmailValues,
    { setSubmitting }: FormikHelpers<any>
  ) => {
    setEmail(emails);
    const values = {
      emails: emails.emailList,
      subscription_id: userInfo.subscription_id,
      purchaser_name: purchaserInfo.purchaser_name,
      purchaser_email: purchaserInfo.purchaser_email,
    };
    setSubmitting(false);
    await handleEmails.mutateAsync(values);
  };

  const handleRedeemCode = useMutation({
    mutationFn: async (code: ICode) => {
      // Try registration code first (silently), then fall back to gift invitation
      try {
        const regData = await validateRegistrationCode(code, true);
        return { type: "registration" as const, data: regData };
      } catch {
        // Registration code failed silently, try gift invitation
        const giftData = await validateGiftInvitation(code, true);
        return { type: "gift" as const, data: giftData };
      }
    },
    retry: 0,
    onSuccess: (result) => {
      setActiveRegStep(registrationEnum.user);

      if (result.type === "registration") {
        displaySuccess(
          `Code validated! You'll receive: ${result.data.subscription_name}. Please sign up to get started.`
        );
        setUserInfo((prev) => ({
          ...prev,
          subscription_id: result.data.subscription_id,
          registration_code: result.data.registration_code_id,
        }));
      } else {
        displaySuccess(
          "You have successfully redeemed invitation code. Please Signup and get started."
        );
        setUserInfo((prev) => ({
          ...prev,
          subscription_id: result.data.subscription_id,
          invitation_id: result.data.invitation_id,
          email: result.data.email,
        }));
      }
    },
    onError: () => {
      displayError("Invalid code. Please check your code and try again.");
    },
  });

  const handleRedeemCodeSubmit = async (code: string) => {
    setRedeemCode(code);
  };

  const generateYearOptions = (
    startYear: number,
    endYear: number
  ): SelectOption[] => {
    const options: SelectOption[] = [];
    for (let year = endYear; year >= startYear; year--) {
      options.push({ value: year.toString(), label: year.toString() });
    }
    return options;
  };

  //calculate how many years to show for dob
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - 100;
  const options = generateYearOptions(startYear, currentYear);

  return (
    <>
      <div className={"flex flex-col px-6 py-12 lg:px-8 bg-brand overflow-auto min-h-[calc(100vh-56px)]"}>

        <div>
          {activeRegStep !== registrationEnum.payment &&
            activeRegStep !== registrationEnum.user &&
            activeRegStep !== registrationEnum.purchaser && (
              <Tabs
                items={tabsList}
                onClick={tabChanged}
                activeIndex={activeTab}
                opacity={true}
              />
            )}
        </div>
        <div className="w-full h-full">
          {activeRegStep === registrationEnum.subscription &&
            activeTab !== 2 && (
              <ChoosePlan
                status={status}
                onPlanSelect={onPlanSelect}
                subscriptionList={subscriptionList}
                activeTab={activeTab}
              />
            )}
          {activeRegStep === registrationEnum.user &&
            (activeTab === 0 || activeTab === 2) && (
              <RegisterUser
                userInfo={userInfo}
                handleValueChanged={handleValueChanged}
                handleSubmit={handleSubmit}
                options={options}
              />
            )}
          {activeRegStep === registrationEnum.purchaser && activeTab === 1 && (
            <PurchaserInfo
              setActiveRegStep={setActiveRegStep}
              handlePurchaserSubmit={handlePurchaserSubmit}
              initialValues={purchaserInfo}
            />
          )}
          {activeRegStep === registrationEnum.user && activeTab === 1 && (
            <HelpSomeoneLearn
              setActiveRegStep={setActiveRegStep}
              setActiveTab={setActiveTab}
              handleGiftInvitation={handleGiftInvitation}
              email={email}
              purchaserInfo={purchaserInfo}
            />
          )}
          {activeRegStep === registrationEnum.payment && (
            <PaymentScreen
              onGoback={() => {
                setActiveRegStep(registrationEnum.user);
              }}
              clientSecret={clientSecret}
              email={
                activeTab === 1 ? purchaserInfo.purchaser_email : userInfo.email
              }
              priceInfo={priceInfo}
              isGift={activeTab === 1}
              recipientEmails={activeTab === 1 ? email.emailList : []}
              onSuccess={async () => {
                displaySuccess(
                  "Payment complete. Please check your email to verify your account before logging in."
                );
                await localStorage.removeItem("userInfo");
                navigate("/register-success", {
                  state: {
                    isGift: activeTab === 1,
                    recipientEmails: activeTab === 1 ? email.emailList : [],
                  },
                });
              }}
            />
          )}
          {activeTab === 2 && activeRegStep !== registrationEnum.user && (
            <RedeemCode
              handleRedeemCodeSubmit={handleRedeemCodeSubmit}
              redeemCode={redeemCode}
              handleSubmit={() => {
                handleRedeemCode.mutateAsync({
                  code: redeemCode,
                }).catch(() => {});
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default RegisterScreen;
